import { Component, OnInit } from '@angular/core';
import { GermplasmDto } from '../../shared/germplasm/model/germplasm.model';
import { GermplasmImportContext } from './germplasm-import.context';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { HEADERS } from './germplasm-import.component';
import { animate, style, transition, trigger } from '@angular/animations';
import { toUpper } from '../../shared/util/to-upper';
import { NameType } from '../../shared/germplasm/model/name-type.model';
import { FORCE_CREATE_PERMISSIONS } from '../../shared/auth/permissions';
import { Principal } from '../../shared';
import { GermplasmDetailsPopupService } from '../../germplasm-details/germplasm-details-popup.service';

/**
 * Return matches for a row and nameTypes. If same germplasm match by more than 1 name, return a single copy
 */
export function getRowMatches(
    row,
    nametypes: NameType[],
    matchesByName: { [key: string]: GermplasmDto[]; }
): GermplasmDto[] {
    const gidMap = {};
    return nametypes
        .filter((nameType) => Boolean(matchesByName[toUpper(row[nameType.code])]))
        .reduce((array, nameType) => array.concat(matchesByName[toUpper(row[nameType.code])]), [])
        // dedup
        .filter((germplasm: GermplasmDto) => {
            if (!gidMap[germplasm.gid]) {
                gidMap[germplasm.gid] = true;
                return true;
            }
            return false;
        });
}

@Component({
    selector: 'jhi-germplasm-import-matches',
    templateUrl: 'germplasm-import-matches.component.html',
    animations: [
        trigger('tableAnimation', [
            transition('void => *', [
                style({ opacity: 0 }),
                animate(500)
            ]),
        ])
    ]
})
export class GermplasmImportMatchesComponent implements OnInit {

    HEADERS = HEADERS;

    page = 0;
    pageSize = 10;

    // modal input
    unassignedMatches: any[] = [];
    matchesByName: { [key: string]: GermplasmDto[] };
    // ENTRY_NO -> gid or null (new)
    selectMatchesResult: { [key: string]: number };

    // internal usage
    matchIndex = 0;
    matchNumber = 1;
    unassignedCount: number;
    matches: GermplasmDto[] = [];
    dataRow: any = {};
    entryNo: string;
    name: string;
    useSameMatchForAllOccurrences = true;
    isIgnoreAllRemaining: boolean;
    isIgnoreMatch: boolean;
    selectedGermplasm: GermplasmDto;
    assignedMatchIndexes: any[] = [];
    sameGermplasmMatches: { [key: string]: number[] } = {};

    constructor(
        private context: GermplasmImportContext,
        private modal: NgbActiveModal,
        private principal: Principal,
        protected germplasmDetailsPopupService: GermplasmDetailsPopupService
    ) {
    }

    ngOnInit(): void {
        this.unassignedCount = this.unassignedMatches.length;
        this.processMatch();

    }

    private processMatch() {
        this.page = 0;
        this.isIgnoreMatch = false;
        this.dataRow = this.unassignedMatches[this.matchIndex];
        this.matches = getRowMatches(this.dataRow, this.context.nametypesCopy, this.matchesByName);
        this.entryNo = this.dataRow[HEADERS.ENTRY_NO];
    }

    onSelectMatch(germplasm: GermplasmDto) {
        this.selectedGermplasm = germplasm;
        this.selectMatchesResult[this.dataRow[HEADERS.ENTRY_NO]] = germplasm.gid;
        this.isIgnoreMatch = false;
        this.isIgnoreAllRemaining = false;
    }

    onUseSameMatchForAllOccurrences() {
        if (this.useSameMatchForAllOccurrences) {
            this.isIgnoreMatch = false;
            this.isIgnoreAllRemaining = false;
        }
    }

    onIgnoreMatch() {
        if (this.isIgnoreMatch) {
            this.useSameMatchForAllOccurrences = false;
            this.selectMatchesResult[this.dataRow[HEADERS.ENTRY_NO]] = null;
        }
    }

    onIgnoreAllRemaining() {
        if (this.isIgnoreAllRemaining) {
            this.useSameMatchForAllOccurrences = false;
            // we consider also this match as "remaining"
            this.isIgnoreMatch = true;
            this.selectMatchesResult[this.dataRow[HEADERS.ENTRY_NO]] = null;
        }
    }

    dismiss() {
        this.modal.dismiss();
    }

    back() {
        if (this.assignedMatchIndexes.length === 0) {
            this.modal.dismiss();
        } else {
            // get the last matched number
            this.matchIndex = this.assignedMatchIndexes.pop();
            this.matchNumber = this.matchIndex + 1;
            const currentDataRow = this.unassignedMatches[this.matchIndex];
            this.selectMatchesResult[currentDataRow[HEADERS.ENTRY_NO]] = null;
            if (this.sameGermplasmMatches[this.matchIndex]) {
                this.sameGermplasmMatches[this.matchIndex].forEach((matchIndex) => {
                    // remove the set matched gid for the corresponding data row
                    const dataRow = this.unassignedMatches[matchIndex];
                    this.selectMatchesResult[dataRow[HEADERS.ENTRY_NO]] = null;
                });
                this.sameGermplasmMatches[this.matchIndex] = null;
            }
            this.processMatch();
        }
    }

    next() {
        if (this.isFinish()) {
            this.modal.close(this.selectMatchesResult);
            return;
        }
        this.assignedMatchIndexes.push(this.matchIndex);
        if (this.useSameMatchForAllOccurrences) {
            this.setSelectedGermplasmForAllOccurrences();
        }
        this.matchIndex = this.getNextMatchIndex();
        this.matchNumber = this.matchIndex + 1;
        if (this.matchIndex === this.unassignedCount) {
            this.modal.close(this.selectMatchesResult);
            return;
        }
        this.processMatch();
    }

    setSelectedGermplasmForAllOccurrences() {
        // Get all the names of the selected germplasm
        const germplasmNames = [];
        this.selectedGermplasm.names.forEach((germplasmName) => {
            germplasmNames.push(toUpper(germplasmName.name));
        });

        // Get the names that matched from the current row and the selected germplasm
        const currentRowNames = this.getRowNames(this.dataRow);
        const matchedNames = currentRowNames.filter(function(rowName) {
            return germplasmNames.indexOf(rowName) !== -1;
        });

        // Set the selected germplasm for entries that has any of the name matches from the current row and selected germplasm
        for (let i = this.matchNumber; i < this.unassignedCount; i++ ) {
            const dataRow = this.unassignedMatches[i];
            if (!this.selectMatchesResult[dataRow[HEADERS.ENTRY_NO]]) {
                const dataRowNames = this.getRowNames(dataRow);
                if (this.hasMatchingNames(dataRowNames, matchedNames)) {
                    this.selectMatchesResult[dataRow[HEADERS.ENTRY_NO]] = this.selectedGermplasm.gid;
                    if (!this.sameGermplasmMatches[this.matchIndex]) {
                        this.sameGermplasmMatches[this.matchIndex] = [];
                    }
                    this.sameGermplasmMatches[this.matchIndex].push(i);
                }
            }
        }

    }

    hasMatchingNames(currentRowNames: any[], matchedNames: any[]): boolean {
        return currentRowNames.filter(function(rowName) {
            return matchedNames.indexOf(rowName) !== -1;
        }).length !== 0;
    }

    getRowNames(dataRow: any): any[] {
        const dataRowNames = [];
        this.context.nametypesCopy.forEach((nameType) => {
            dataRowNames.push(toUpper(dataRow[toUpper(nameType.code)]));
        });
        return dataRowNames;
    }

    hasForceCreatePermission() {
        return this.principal.hasAnyAuthorityDirect(FORCE_CREATE_PERMISSIONS);
    }

    getNextMatchIndex(): number {
        // increment matchNumber until the corresponding data row has no set matched result
        for (let i = this.matchNumber; i < this.unassignedCount; i++ ) {
            const dataRow = this.unassignedMatches[i];
            if (!this.selectMatchesResult[dataRow[HEADERS.ENTRY_NO]]) {
                return i;
            }
        }
        return this.unassignedCount;
    }

    size(obj) {
        return Object.keys(obj).length;
    }

    isFinish() {
        return this.matchIndex === this.unassignedCount
            || this.isIgnoreAllRemaining;
    }
}


